import { useState, useCallback } from 'react'
import {
	getFCMToken,
	requestNotificationPermission,
	isFCMSupported,
} from '@/modules/notification/utils/firebase'
import axiosInstance from '@/core/api'

interface UseFCMTokenReturn {
	isLoading: boolean
	error: string | null
	requestPermissionAndRegisterToken: () => Promise<string | null>
	deleteToken: (token: string) => Promise<void>
	checkPermissionStatus: () => NotificationPermission
}

export const useFCMToken = (): UseFCMTokenReturn => {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const checkPermissionStatus = useCallback((): NotificationPermission => {
		if (!('Notification' in window)) {
			return 'denied'
		}
		return Notification.permission
	}, [])

	const registerTokenWithBackend = async (token: string): Promise<void> => {
		try {
			// Generate a unique device ID for this browser
			const deviceId = await getDeviceId()

			await axiosInstance.post('/admin/operators/me/fcm-token', {
				token,
				deviceId,
				platform: 'web',
				deviceInfo: getBrowserInfo(),
			})
			console.log('FCM token registered successfully')
		} catch (error) {
			console.error('Error registering FCM token:', error)
			throw error
		}
	}

	const deleteTokenFromBackend = async (token: string): Promise<void> => {
		try {
			await axiosInstance.delete(
				`/admin/operators/me/fcm-token/${encodeURIComponent(token)}`,
			)
			console.log('FCM token deleted successfully')
		} catch (error) {
			console.error('Error deleting FCM token:', error)
			throw error
		}
	}

	const requestPermissionAndRegisterToken = useCallback(async (): Promise<
		string | null
	> => {
		setIsLoading(true)
		setError(null)

		try {
			// Check if FCM is supported
			if (!isFCMSupported()) {
				throw new Error('FCM is not supported on this platform')
			}

			// Request notification permission if not already granted
			let permission = checkPermissionStatus()
			if (permission === 'default') {
				permission = await requestNotificationPermission()
			}

			if (permission !== 'granted') {
				throw new Error(`Notification permission ${permission}`)
			}

			// Get FCM token
			const token = await getFCMToken()
			if (!token) {
				throw new Error('Failed to get FCM token')
			}

			// Register token with backend
			await registerTokenWithBackend(token)

			return token
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Unknown error occurred'
			setError(errorMessage)
			console.error('Error in requestPermissionAndRegisterToken:', errorMessage)
			return null
		} finally {
			setIsLoading(false)
		}
	}, [checkPermissionStatus])

	const deleteToken = useCallback(async (token: string): Promise<void> => {
		setIsLoading(true)
		setError(null)

		try {
			await deleteTokenFromBackend(token)
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Unknown error occurred'
			setError(errorMessage)
			console.error('Error in deleteToken:', errorMessage)
			throw err
		} finally {
			setIsLoading(false)
		}
	}, [])

	return {
		isLoading,
		error,
		requestPermissionAndRegisterToken,
		deleteToken,
		checkPermissionStatus,
	}
}

/**
 * Generate a unique device ID for this browser
 * Uses a combination of browser fingerprint and localStorage
 */
async function getDeviceId(): Promise<string> {
	const storageKey = 'fcm_device_id'

	// Check if we already have a device ID
	const existingId = localStorage.getItem(storageKey)
	if (existingId) {
		return existingId
	}

	// Generate a new device ID
	const deviceId = `web-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
	localStorage.setItem(storageKey, deviceId)

	return deviceId
}

/**
 * Get browser information for device info
 */
function getBrowserInfo(): string {
	const ua = navigator.userAgent
	let browserName = 'Unknown'
	let browserVersion = ''

	if (ua.includes('Firefox/')) {
		browserName = 'Firefox'
		browserVersion = ua.split('Firefox/')[1]?.split(' ')[0] || ''
	} else if (ua.includes('Chrome/')) {
		browserName = 'Chrome'
		browserVersion = ua.split('Chrome/')[1]?.split(' ')[0] || ''
	} else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
		browserName = 'Safari'
		browserVersion = ua.split('Version/')[1]?.split(' ')[0] || ''
	} else if (ua.includes('Edge/')) {
		browserName = 'Edge'
		browserVersion = ua.split('Edge/')[1]?.split(' ')[0] || ''
	}

	const os = getOSInfo()

	return `${browserName} ${browserVersion} on ${os}`
}

/**
 * Get OS information
 */
function getOSInfo(): string {
	const ua = navigator.userAgent

	if (ua.includes('Windows')) return 'Windows'
	if (ua.includes('Mac OS')) return 'macOS'
	if (ua.includes('Linux')) return 'Linux'
	if (ua.includes('Android')) return 'Android'
	if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad'))
		return 'iOS'

	return 'Unknown OS'
}
